import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react";
import axiosInstance from "../Config/axios";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Loader from "../Components/Loader/Loader";
import { useNewUser } from "./NewUserContext";

const InstructionsContext = createContext();
export const useInstructions = () => useContext(InstructionsContext);

export const InstructionsContextProvider = ({ children }) => {
  // const { fetchCartCount } = useNewUser();
  const [packages, setPackages] = useState([]);
  const [packageCategories, setPackageCategories] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const toastRef = useRef(null);
  const navigate = useNavigate();
  const time_Zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const clearPackages = () => setPackages([]);

  const handleUnauthorized = () => {
    toastRef.current?.show({
      severity: "error",
      summary: "Unauthorized",
      detail: "Access token missing. Please log in again.",
      life: 4000,
    });
    clearPackages();
    localStorage.clear();
    navigate("/login", { replace: true });
  };

  /** ✅ Fetch all packages */
  const fetchGroupInstructions = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/customer/packages/", {
        params: { timezone: time_Zone },
      });
      setPackages(response.data || []);
    } catch (err) {
      const errorDetail =
        err.response?.data?.detail || "Failed to fetch packages";
      // setError(errorDetail);
      // toastRef.current?.show({
      //   severity: "error",
      //   summary: "Error",
      //   detail: errorDetail,
      //   life: 3000,
      // });

      console.log("Error fetching packages:", err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchInstructionCategories = useCallback(
    async (id) => {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) return handleUnauthorized();

      setLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.get(
          `/customer/packages/category/${id}/`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { timezone: time_Zone },
          },
        );
        setPackageCategories(response.data || []);
      } catch (err) {
        const errorDetail =
          err.response?.data?.detail || "Failed to fetch packages";
        // setError(errorDetail);
        // toastRef.current?.show({
        //   severity: "error",
        //   summary: "Error",
        //   detail: errorDetail,
        //   life: 3000,
        // });
        console.log("Error fetching package categories:", errorDetail);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const fetchCartItems = async (id) => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return handleUnauthorized();

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(`customer/new/list-cart/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { timezone: time_Zone },
      });
      setCartItems(response.data || []);
    } catch (err) {
      const errorDetail =
        err.response?.data?.detail || "Failed to fetch packages";
      // setError(errorDetail);
      // toastRef.current?.show({
      //   severity: "error",
      //   summary: "Error",
      //   detail: errorDetail,
      //   life: 3000,
      // });
      console.log("Error fetching cart items:", errorDetail);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (data) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/customer/cart/add/new/",
        data,
        {
          params: { timezone: time_Zone },
        },
      );
      // setParentProfile(response.data);
      Swal.fire({
        icon: "success",
        title: "Added to Cart!",
        text: "Your session has been added to the cart successfully.",
        showConfirmButton: true,
        confirmButtonText: "Go to Cart",
        showCancelButton: true,
        cancelButtonText: "Continue Shopping",
        confirmButtonColor: "#f7931a",
        cancelButtonColor: "#1C1C1C",
        reverseButtons: true,
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "/cart";
        }
        if (result.isDismissed) {
          // Do nothing, just close the modal
          window.location.href = "/instruction/";
        }
      });

      return { success: true, data: response.data };
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err.response?.data?.detail || "Something went wrong.",
        confirmButtonColor: "#f7931a",
        cancelButtonColor: "#1C1C1C",
      });

      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (id) => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return handleUnauthorized();

    setLoading(true);
    setError(null);

    const data = { cart_item_id: id };

    const headers = { Authorization: `Bearer ${accessToken}` };

    try {
      const response = await axiosInstance.post(
        `/customer/new/cart-remove/`,
        data,
        {
          headers,
        },
      );
      // await fetchCartItems();
      // await fetchCartCount();
      // Swal.fire({
      //   icon: "success",
      //   title: "Success !",
      //   text: `${response.data?.message || "Cart Item deleted successfully."}`,
      //   confirmButtonColor: "#f7931a",
      //   cancelButtonColor: "#1C1C1C",
      // });

      Swal.fire({
        icon: "success",
        title: "Deleted from Cart!",
        text: "Your item has been deleted from the cart successfully.",
        showConfirmButton: true,
        confirmButtonText: "Go to Cart",
        showCancelButton: true,
        cancelButtonText: "Continue Shopping",
        confirmButtonColor: "#f7931a",
        cancelButtonColor: "#1C1C1C",
        reverseButtons: true,
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "/cart";
        }

        if (result.dismiss === Swal.DismissReason.cancel) {
          window.location.href = "/instruction/";
        }
      });
      return { success: true, data: response.data };
    } catch (err) {
      const errorDetail = err.response?.data || {
        detail: "Failed to delete Item",
      };
      Swal.fire({
        icon: "error",
        title: "Error !",
        text: `${errorDetail.detail || "An error occurred while deleting the Item."}`,
        confirmButtonColor: "#f7931a",
        cancelButtonColor: "#1C1C1C",
      });
      return { success: false, error: errorDetail };
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return handleUnauthorized();

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(
        `/customer/new/list-user-coupons/`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      setCoupons(response.data || []);
    } catch (err) {
      const errorDetail =
        err.response?.data?.detail || "Failed to fetch packages";
      // setError(errorDetail);
      // toastRef.current?.show({
      //   severity: "error",
      //   summary: "Error",
      //   detail: errorDetail,
      //   life: 3000,
      // });
      console.log("Error fetching coupons:", errorDetail);
    } finally {
      setLoading(false);
    }
  };

  const applyCoupon = async (data) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/customer/v2/apply-coupon/",
        data,
        {
          params: { timezone: time_Zone },
        },
      );
      return { success: true, data: response.data };
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error !",
        text: `${err.detail || "Invalid coupon"}`,
        confirmButtonColor: "#f7931a",
        cancelButtonColor: "#1C1C1C",
      });
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const proceedToCheckout = async (data) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/payment/new/checkout/",
        data,
        {
          params: { timezone: time_Zone },
        },
      );

      return { success: true, data: response.data };
    } catch (err) {
      // Swal.fire({
      //   icon: "error",
      //   title: "Failed",
      //   text: err.response?.data?.detail || "Something went wrong.",
      //   confirmButtonColor: "#f7931a",
      //   cancelButtonColor: "#1C1C1C",
      // });
      console.log(err.response?.data?.detail || "Something went wrong.");
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const verifyPaymentStatus = async (id) => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return handleUnauthorized();

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(
        `/payment/verify_payment/?session_id=${id}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { timezone: time_Zone },
        },
      );

      return response.data;
    } catch (err) {
      const errorDetail = err.response?.data?.detail || "Something went wrong";
      // setError(errorDetail);
      // toastRef.current?.show({
      //   severity: "error",
      //   summary: "Error",
      //   detail: errorDetail,
      //   life: 3000,
      // });
      console.log("Error verifying payment status:", errorDetail);
    } finally {
      setLoading(false);
    }
  };

  const removeCupon = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post(`/customer/v2/remove-coupon/`);
      await fetchCartItems();
      Swal.fire({
        icon: "success",
        title: "Success !",
        text: `${response.data?.message || "Coupon removed successfully"}`,
        confirmButtonColor: "#f7931a",
        cancelButtonColor: "#1C1C1C",
      });
      return { success: true, data: response.data };
    } catch (err) {
      const errorDetail = err.response?.data || {
        detail: "Failed to delete Item",
      };
      Swal.fire({
        icon: "error",
        title: "Error !",
        text: `${errorDetail.detail || "An error occurred while removeing the Coupon."}`,
        confirmButtonColor: "#f7931a",
        cancelButtonColor: "#1C1C1C",
      });
      return { success: false, error: errorDetail };
    } finally {
      setLoading(false);
    }
  };

  return (
    <InstructionsContext.Provider
      value={{
        packages,
        packageCategories,
        cartItems,
        coupons,
        fetchCartItems,
        addToCart,
        removeFromCart,
        loading,
        error,
        fetchGroupInstructions,
        fetchInstructionCategories,
        fetchCoupons,
        applyCoupon,
        proceedToCheckout,
        verifyPaymentStatus,
        removeCupon,
      }}
    >
      {loading && <Loader />}
      {children}
    </InstructionsContext.Provider>
  );
};
